import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";

import { AuthLayout } from "../../../auth";
import PageMeta from "../../../../shared/ui/common/PageMeta";
import Button from "../../../../shared/ui/button/Button";
import PlusSubscriptionCard from "../components/PlusSubscriptionCard";

/**
 * The account's own subscription — as opposed to `PricingPage`, which is the
 * public comparison of the two plans.
 *
 * Stripe returns the browser here after Checkout with `?checkout=success` or
 * `?checkout=cancelled`. The banner it produces says the payment is being
 * confirmed, never that Plus is on: the plan moves when the webhook lands, and
 * this redirect proves only that somebody navigated.
 */
export default function BillingPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [banner, setBanner] = useState<"success" | "cancelled" | null>(null);

    // Consumed on arrival, so a refresh does not replay a message already read.
    useEffect(() => {
        const outcome = searchParams.get("checkout");
        if (outcome !== "success" && outcome !== "cancelled") return;

        setBanner(outcome);
        const next = new URLSearchParams(searchParams);
        next.delete("checkout");
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);

    return (
        // Standalone, like the pricing and comparison pages beside it: these
        // routes sit outside the app shell, so the page brings its own frame —
        // the same one, logo included, that `/subscription` arrives from.
        <div className="relative min-h-screen w-full overflow-y-auto bg-gray-50 dark:bg-gray-900">
            <PageMeta
                title={t("subscription.billing.title")}
                description={t("subscription.billing.description")}
            />

            <AuthLayout hideLogo={false} hideVisualPanel={true} wrapperClassName="max-w-2xl">
                <div className="w-full space-y-5">
                    <h1 className="text-2xl font-semibold tracking-tight text-content-primary">
                        {t("subscription.billing.title")}
                    </h1>

                    {banner && (
                        <p
                            role="status"
                            className={`rounded-lg p-3 text-sm ${
                                banner === "success"
                                    ? "bg-green-50 text-green-800 dark:bg-green-500/10 dark:text-green-300"
                                    : "bg-gray-50 text-content-secondary dark:bg-gray-800"
                            }`}
                        >
                            {t(`subscription.billing.checkout.${banner}`)}
                        </p>
                    )}

                    <p className="text-sm text-content-tertiary">
                        {t("subscription.billing.description")}
                    </p>

                    <PlusSubscriptionCard />

                    <Button variant="outline" onClick={() => navigate("/subscription/compare")}>
                        {t("subscription.compare.link")}
                    </Button>
                </div>
            </AuthLayout>
        </div>
    );
}
